import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Settings.css';

interface UserSettings {
  auto_play_next: number;
  skip_intro: number;
  auto_mute_on_tab_switch: number;
  default_volume: number;
  playback_speed: string;
  video_quality: string;
}

interface NotificationPreferences {
  email_notifications: number;
  browser_notifications: number;
  new_content_enabled: number;
  party_invite_enabled: number;
  coins_enabled: number;
  system_enabled: number;
  newsletter: number;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [activeTab, setActiveTab] = useState<'playback' | 'notifications'>('playback');
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  
  const [settings, setSettings] = useState<UserSettings>({
    auto_play_next: 0,
    skip_intro: 0,
    auto_mute_on_tab_switch: 0,
    default_volume: 80,
    playback_speed: '1.0',
    video_quality: 'auto'
  });
  
  const [notifications, setNotifications] = useState<NotificationPreferences>({
    email_notifications: 1,
    browser_notifications: 1,
    new_content_enabled: 1,
    party_invite_enabled: 1,
    coins_enabled: 1,
    system_enabled: 1,
    newsletter: 0
  });

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/settings');
    }
  }, [isAuthenticated, navigate]);

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Load user settings
      const settingsRes = await fetch('/api/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        if (settingsData.data) {
          setSettings(prev => ({ ...prev, ...settingsData.data }));
        }
      }

      // Load notification preferences
      const notifRes = await fetch('/api/notifications/preferences', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (notifRes.ok) {
        const notifData = await notifRes.json();
        if (notifData.data) {
          setNotifications(prev => ({ ...prev, ...notifData.data }));
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save setting
  const saveSetting = async (key: string, value: any) => {
    try {
      
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch('/api/settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });

      if (response.ok) {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);
      }
    } catch (error) {
      console.error('Failed to save setting:', error);
    } finally {
      
    }
  };

  // Save notification preference
  const saveNotificationPreference = async (key: string, value: number) => {
    try {
      
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch('/api/notifications/preferences', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });

      if (response.ok) {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);
      }
    } catch (error) {
      console.error('Failed to save notification preference:', error);
    } finally {
      
    }
  };

  // Handle toggle change
  const handleToggleChange = (key: keyof UserSettings) => {
    const newValue = settings[key] ? 0 : 1;
    setSettings(prev => ({ ...prev, [key]: newValue }));
    saveSetting(key, newValue);
  };

  // Handle notification toggle change
  const handleNotificationToggle = (key: keyof NotificationPreferences) => {
    const newValue = notifications[key] ? 0 : 1;
    setNotifications(prev => ({ ...prev, [key]: newValue }));
    saveNotificationPreference(key, newValue);
  };

  // Handle select change
  const handleSelectChange = (key: keyof UserSettings, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    saveSetting(key, value);
  };

  // Handle volume change
  const handleVolumeChange = (value: number) => {
    setSettings(prev => ({ ...prev, default_volume: value }));
  };

  const handleVolumeCommit = () => {
    saveSetting('default_volume', settings.default_volume);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="settings-page">
      <div className="settings-container">
        {/* Loading Spinner */}
        {loading && (
          <div className="settings-loading">
            <div className="spinner"></div>
          </div>
        )}

        {/* Header */}
        <div className="settings-header">
          <h1>Settings</h1>
          <p>Customize your CINECLUBS experience</p>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`tab-btn ${activeTab === 'playback' ? 'active' : ''}`}
            onClick={() => setActiveTab('playback')}
          >
            <i className="fas fa-play-circle"></i>
            <span>Playback</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <i className="fas fa-bell"></i>
            <span>Notifications</span>
          </button>
        </div>

        {/* Playback Tab */}
        {activeTab === 'playback' && (
          <div className="tab-content active">
            <div className="settings-card">
              <h3 className="card-title">Video Settings</h3>
              
              <div className="switch-row">
                <div className="switch-info">
                  <h4>Auto-Play Next Episode</h4>
                  <p>Automatically play next video when current finishes</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.auto_play_next)}
                    onChange={() => handleToggleChange('auto_play_next')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>Skip Intro</h4>
                  <p>Show skip button during opening credits</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.skip_intro)}
                    onChange={() => handleToggleChange('skip_intro')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>Auto-Mute on Tab Switch</h4>
                  <p>Mute video when switching browser tabs</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.auto_mute_on_tab_switch)}
                    onChange={() => handleToggleChange('auto_mute_on_tab_switch')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="settings-card">
              <h3 className="card-title">Playback Preferences</h3>
              
              <div className="form-group">
                <label className="form-label">
                  Default Volume <span className="range-value">{settings.default_volume}%</span>
                </label>
                <div className="range-slider">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={settings.default_volume}
                    onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                    onMouseUp={handleVolumeCommit}
                    onTouchEnd={handleVolumeCommit}
                  />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Playback Speed</label>
                  <select
                    className="form-control"
                    value={settings.playback_speed}
                    onChange={(e) => handleSelectChange('playback_speed', e.target.value)}
                  >
                    <option value="0.5">0.5x</option>
                    <option value="0.75">0.75x</option>
                    <option value="1.0">1.0x (Normal)</option>
                    <option value="1.25">1.25x</option>
                    <option value="1.5">1.5x</option>
                    <option value="2.0">2.0x</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Video Quality</label>
                  <select
                    className="form-control"
                    value={settings.video_quality}
                    onChange={(e) => handleSelectChange('video_quality', e.target.value)}
                  >
                    <option value="auto">Auto</option>
                    <option value="1080p">1080p</option>
                    <option value="720p">720p</option>
                    <option value="480p">480p</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="tab-content active">
            <div className="settings-card">
              <h3 className="card-title">Notification Preferences</h3>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>Email Notifications</h4>
                  <p>Receive updates and alerts via email</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(notifications.email_notifications)}
                    onChange={() => handleNotificationToggle('email_notifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>Browser Notifications</h4>
                  <p>Get desktop push notifications</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(notifications.browser_notifications)}
                    onChange={() => handleNotificationToggle('browser_notifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>New Content Alerts</h4>
                  <p>Notify when new movies or series are added</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(notifications.new_content_enabled)}
                    onChange={() => handleNotificationToggle('new_content_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>Watch Party Invites</h4>
                  <p>Receive notifications for party invitations</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(notifications.party_invite_enabled)}
                    onChange={() => handleNotificationToggle('party_invite_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>Coins & Subscription Alerts</h4>
                  <p>Get notified about coins and billing updates</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(notifications.coins_enabled)}
                    onChange={() => handleNotificationToggle('coins_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>System Notifications</h4>
                  <p>Receive system messages and updates</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(notifications.system_enabled)}
                    onChange={() => handleNotificationToggle('system_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="switch-row">
                <div className="switch-info">
                  <h4>Newsletter</h4>
                  <p>Receive periodic newsletters and updates</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(notifications.newsletter)}
                    onChange={() => handleNotificationToggle('newsletter')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save Toast */}
      <div className={`save-toast ${showToast ? 'show' : ''}`}>
        <i className="fas fa-check-circle"></i>
        <span>Settings saved!</span>
      </div>
    </div>
  );
};

export default Settings;
